import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { CLIENT_CONTEXT_LOAD_TIMEOUT_MS } from '@core/constants';
import { IClientContext } from '@core/models';
import { Logger } from '@core/services/logger';
import { getSafeErrorDetails } from '@core/utils';

const log = new Logger('AppConfig');

@Injectable({ providedIn: 'root' })
export class AppConfig {
  private readonly clientContextCache = new Map<string, IClientContext>();
  private readonly pendingClientContextLoads = new Map<
    string,
    Promise<IClientContext | undefined>
  >();

  constructor(private http: HttpClient) {}

  async initialize(): Promise<void> {
    return Promise.resolve();
  }

  getCachedClientName(clientId: string): string | undefined {
    return this.clientContextCache.get(clientId)?.name;
  }

  loadClientContext(clientId: string): Promise<IClientContext | undefined> {
    const cached = this.clientContextCache.get(clientId);
    if (cached) {
      return Promise.resolve(cached);
    }

    const pendingLoad = this.pendingClientContextLoads.get(clientId);
    if (pendingLoad) {
      return pendingLoad;
    }

    const contextLoad = this.fetchClientContext(clientId).finally(() => {
      this.pendingClientContextLoads.delete(clientId);
    });
    this.pendingClientContextLoads.set(clientId, contextLoad);

    return contextLoad;
  }

  private async fetchClientContext(clientId: string): Promise<IClientContext | undefined> {
    let context: IClientContext | undefined;

    try {
      context = await firstValueFrom(
        this.http
          .get<IClientContext>('/client-context', {
            params: { client_id: clientId }
          })
          .pipe(timeout(CLIENT_CONTEXT_LOAD_TIMEOUT_MS))
      );
    } catch (error) {
      if (error instanceof TimeoutError) {
        log.debug('Client context load timed out; continuing without product context');
        return undefined;
      }

      log.warn('Client context load failed', getSafeErrorDetails(error));
      return undefined;
    }

    if (!this.isExpectedClientContext(context, clientId)) {
      return undefined;
    }

    this.clientContextCache.set(clientId, context);
    return context;
  }

  private isExpectedClientContext(
    context: IClientContext | undefined,
    clientId: string
  ): context is IClientContext {
    return (
      context?.clientId === clientId &&
      typeof context.name === 'string' &&
      context.name.trim().length > 0
    );
  }
}
